import ReactDatePicker from 'react-datepicker';
import React, {Suspense, useEffect, useRef, useState} from 'react';
import {isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';
import clsx from 'clsx';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useTripDayKr, {getWeekIndexOfDate, isTripOnDay} from '@di-shared/features/history/date/useTripDay.kr';
import {HistoryDateCalendarProps} from '@di-shared/features/history/date/HistoryDateCalendar/index';
import {ErrorBoundary} from 'react-error-boundary';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export default function ({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          isMonthMode={isMonthMode}
          isMonthChange={isMonthChange}
          setIsMonthChange={setIsMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, setSelectedDate, isMonthMode, isMonthChange, setIsMonthChange}: HistoryDateCalendarProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  // viewDate: 달력이 표시 중인 월을 추적. selectedDate와 분리해 onMonthChange 중
  // setSelectedDate(URL navigate를 포함한 외부 콜백)를 호출하지 않도록 한다.
  const [viewDate, setViewDate] = useState(selectedDate);

  const {minDate, tripDays, focusTripDate} = useTripDayKr({vin, selectedDate: viewDate});
  const [weekIndex, setWeekIndex] = useState(getWeekIndexOfDate(selectedDate));
  const focusTripDateRef = useRef(focusTripDate);
  focusTripDateRef.current = focusTripDate;

  // URL 네비게이션(뒤로가기 등)으로 selectedDate가 외부에서 바뀌면 viewDate도 동기화
  useEffect(() => {
    if (!isMonthChange) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  // 월 변경 시에만 focusTripDate(해당 월의 주행 기록이 있는 날짜)로 이동.
  // focusTripDate는 매 렌더마다 새 참조로 생성되므로 dep에 넣으면 setSelectedDate(navigate 포함)
  // 호출이 배칭을 방해해 루프가 생길 수 있어 isMonthChange만 dep으로 두고 ref로 읽는다.
  useEffect(() => {
    if (isMonthChange) {
      setSelectedDate(focusTripDateRef.current);
      setWeekIndex(getWeekIndexOfDate(focusTripDateRef.current));
      setIsMonthChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthChange]);

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      selected={selectedDate}
      minDate={minDate}
      maxDate={today}
      filterDate={(date) => isSameYearAndMonth(date, selectedDate)}
      disabledKeyboardNavigation
      calendarClassName={clsx(!isMonthMode && `show-first-week-${weekIndex + 1}`)}
      onChange={(date: Date) => {
        setWeekIndex(getWeekIndexOfDate(date));
        setSelectedDate(date);
      }}
      onMonthChange={(date) => {
        setIsMonthChange(true);
        setViewDate(date);
      }}
      renderCustomHeader={({date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--previous', {
              'navigation--disabled': prevMonthButtonDisabled,
            })}
            disabled={prevMonthButtonDisabled}
            onClick={() => decreaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className={clsx('react-datepicker__navigation react-datepicker__navigation--next', {
              'navigation--disabled': nextMonthButtonDisabled,
            })}
            disabled={nextMonthButtonDisabled}
            onClick={() => increaseMonth()}
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
      renderDayContents={(dayOfMonth, date: Date) => (
        <>
          {dayOfMonth}
          {isSameYearAndMonth(date, selectedDate) && isTripOnDay(tripDays, dayOfMonth) && (
            <span className="has_data">
              <span className="blind">{t('T10102')}</span>
            </span>
          )}
        </>
      )}
    />
  );
}

function FallbackLoading() {
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="react-datepicker">
      <div className="react-datepicker__month-container skeleton">
        <div className="react-datepicker__header">
          <div className="react-datepicker__current-month skeleton">
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              aria-label="Previous Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">Previous Month</span>
            </button>
            {CldrDatetimeFormatter.format(today, 'yMMM')}
            <button
              type="button"
              className="react-datepicker__navigation react-datepicker__navigation--next navigation--disabled"
              aria-label="Next Month"
            >
              <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">Next Month</span>
            </button>
          </div>
          <div
            className="react-datepicker__day-names"
            style={{height: '260px'}}
          />
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  const startDate = isSameYearAndMonth(selectedDate, today) ? today : startDateOfMonth(selectedDate);

  return (
    <ReactDatePicker
      inline
      locale={getDateFnsLocale()}
      disabledKeyboardNavigation
      selected={startDate}
      onChange={() => {}}
      renderCustomHeader={({date}) => (
        <div className="react-datepicker__current-month">
          <button
            type="button"
            aria-label="Previous Month"
            className="react-datepicker__navigation react-datepicker__navigation--previous"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
          </button>
          {CldrDatetimeFormatter.format(date, 'yMMM')}
          <button
            type="button"
            aria-label="Next Month"
            className="react-datepicker__navigation react-datepicker__navigation--next"
          >
            <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
          </button>
        </div>
      )}
    />
  );
}
